import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../i18n';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { User, Mail, Phone, Globe, Save, Loader2, Camera, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { handleLocalizedInvalid, clearLocalizedInvalid } from '../lib/localizedValidation';

export default function ProfilePage() {
  const { user, updateProfile } = useAuth();
  const { t } = useLanguage();
  const fileInputRef = useRef(null);

  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    phone: user?.phone || '',
    country: user?.country || 'SN',
    avatar_url: user?.avatar_url || '',
  });

  useEffect(() => {
    setFormData({
      full_name: user?.full_name || '',
      phone: user?.phone || '',
      country: user?.country || 'SN',
      avatar_url: user?.avatar_url || '',
    });
  }, [user]);

  const getInitials = (name) => {
    return name?.split(' ').map((part) => part[0]).join('').slice(0, 2).toUpperCase() || 'U';
  };

  const handlePhotoUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error(t('profilePage.invalidPhotoType'));
      event.target.value = '';
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error(t('profilePage.photoTooLarge'));
      event.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        setFormData((previous) => ({ ...previous, avatar_url: reader.result }));
      }
    };
    reader.onerror = () => {
      toast.error(t('profilePage.photoReadError'));
    };
    reader.readAsDataURL(file);
  };

  const handleRemovePhoto = () => {
    setFormData((previous) => ({ ...previous, avatar_url: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateProfile({
        full_name: formData.full_name,
        phone: formData.phone || null,
        country: formData.country,
        avatar_url: formData.avatar_url || null,
      });
      toast.success(t('profilePage.saved'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('profilePage.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="profile-page">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-stone-900 dark:text-stone-50">{t('profilePage.title')}</h1>
        <p className="text-stone-500 mt-1">{t('profilePage.subtitle')}</p>
      </div>

      <Card className="card-neo">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-orange-600" />
            {t('profilePage.accountInfo')}
          </CardTitle>
          <CardDescription>{t('profilePage.accountInfoDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={handleSubmit}
            onInvalidCapture={(e) => handleLocalizedInvalid(e, t)}
            onInputCapture={clearLocalizedInvalid}
            onChangeCapture={clearLocalizedInvalid}
            className="space-y-4"
          >
            <div className="flex flex-col gap-4 rounded-2xl border border-stone-200/80 bg-stone-50/80 p-4 dark:border-stone-800 dark:bg-stone-900/40 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-4">
                <Avatar className="h-20 w-20 ring-4 ring-orange-100 dark:ring-orange-900/40">
                  <AvatarImage src={formData.avatar_url || undefined} alt={t('profilePage.photoAlt')} />
                  <AvatarFallback className="bg-orange-100 text-xl font-semibold text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
                    {getInitials(formData.full_name)}
                  </AvatarFallback>
                </Avatar>
                <div className="space-y-1">
                  <p className="font-medium text-stone-900 dark:text-stone-100">{t('profilePage.photoTitle')}</p>
                  <p className="text-sm text-stone-500">{t('profilePage.photoHint')}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  className="hidden"
                  onChange={handlePhotoUpload}
                />
                <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()}>
                  <Camera className="mr-2 h-4 w-4" />
                  {t('profilePage.uploadPhoto')}
                </Button>
                {formData.avatar_url && (
                  <Button type="button" variant="ghost" onClick={handleRemovePhoto}>
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t('profilePage.removePhoto')}
                  </Button>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="full_name">{t('auth.fullName')}</Label>
              <Input
                id="full_name"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <div className="relative">
                <Mail className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input id="email" value={user?.email || ''} readOnly className="pl-9 text-stone-500" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">{t('auth.phone')}</Label>
              <div className="relative">
                <Phone className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder={t('authPage.phonePlaceholder')}
                  className="pl-9"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="country">{t('auth.country')}</Label>
              <div className="relative">
                <Globe className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2 z-10" />
                <Select
                  value={formData.country}
                  onValueChange={(value) => setFormData({ ...formData, country: value })}
                >
                  <SelectTrigger id="country" className="pl-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SN">🇸🇳 {t('countries.SN')}</SelectItem>
                    <SelectItem value="CA">🇨🇦 {t('countries.CA')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="pt-2">
              <Button type="submit" className="btn-accent w-full sm:w-auto" disabled={saving} data-testid="profile-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                {t('common.save')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
